// Single source of truth for blog SEO metadata + a crawler-friendly full-text
// body (seoBody). Consumed by the React app (blogPosts.jsx), the sitemap, and
// the build-time prerender (scripts/prerender.js). CommonJS so Node can require it.
const SITE_URL = "https://pitcrewcenter.com";

const BLOG_META = [
  {
    slug: "car-making-weird-noise-diagnose",
    title: "Car Making a Weird Noise? Here's How to Diagnose It Fast",
    metaTitle: "Car Making a Weird Noise? Here's How to Diagnose It Fast | Pitcrew",
    metaDescription:
      "Is your car making a weird noise? Learn what squealing, grinding, knocking, and clunking sounds mean — and how to get a fast, accurate diagnosis with Pitcrew's free AI tool.",
    excerpt:
      "Squeal, clunk, or a low hum that wasn't there yesterday? Decode what your car's noises actually mean — and get a fast, accurate diagnosis without the guesswork.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/ba703438-84db-4d0b-a475-05d6e2fbcf55/images/bcb4a2dfaf07b10554c30d307856aa232df82638f5a756f202109221d21022ed.png",
    category: "Diagnostics",
    date: "2026-06-22",
    readTime: "6 min read",
    seoBody: [
      { p: "You're driving along when — there it is. A squeal. A clunk. A low-pitched hum that wasn't there yesterday. Your car is making a weird noise and now you can't unhear it." },
      { p: "Car noises are maddeningly hard to pin down. They change with speed, temperature, and road conditions. This guide will help you decode what your car is telling you — and show you the fastest way to get a real answer without the guesswork." },
      { h2: "Why Car Noise Diagnosis Is Tricky" },
      { p: "A modern car has thousands of moving parts. A worn pad, a failing bearing, a loose heat shield — any can produce a sound that travels and echoes. The same noise can also mean very different things depending on when it happens." },
      { h2: "Common Car Noises and What They Mean" },
      { ul: [
        "Squealing/Squeaking — worn brake pads (when braking), or a slipping serpentine belt or power steering (while driving).",
        "Grinding — metal-on-metal brake contact if it happens when braking (urgent); transmission issues or a wheel bearing if during driving.",
        "Knocking/Ticking — a deep engine knock is serious (low octane or internal damage); light ticking is often low oil or lifter tick.",
        "Clunking — worn suspension over bumps; a failing CV joint during acceleration or tight turns.",
        "Humming/Whirring — usually a wheel bearing (the hum changes with speed, not RPM); also uneven tire wear or transmission.",
        "Rattling — typically a loose heat shield or exhaust hanger; annoying but rarely dangerous.",
      ] },
      { h2: "When to Act Now vs. When You Have a Little Time" },
      { p: "Act immediately for grinding when braking, engine knocking under load, or any noise paired with a warning light. You can usually wait a day or two for a light brake squeal, occasional ticking that clears after warm-up, or an isolated rattle." },
      { p: "When in doubt, treat it as urgent. A $200 repair ignored becomes a $2,000 repair." },
      { h2: "Why Googling a Car Noise Only Gets You So Far" },
      { p: "Searching gets you lists, not a diagnosis. A real diagnosis needs the full context — make, model, mileage, when the noise happens, and what it sounds like. That's where Pitcrew's free AI diagnostic tool comes in." },
      { h2: "Get a Free AI Car Noise Diagnosis with Pitcrew" },
      { p: "Describe your car and the noise in plain English — the AI asks smart follow-up questions and generates an expert-level diagnostic report in minutes. No appointment. No fee." },
      { h2: "How Pitcrew Works" },
      { ul: [
        "Describe the noise in plain English.",
        "Get a clear, prioritized diagnostic report instantly.",
        "Receive competitive bids from vetted local mechanics — compare prices, read reviews, choose who you trust.",
      ] },
      { p: "Your whole team. One click." },
      { h2: "Stop Guessing. Start Knowing." },
      { p: "Get your free AI car noise diagnosis — describe the noise, get the report, and let vetted mechanics compete for your business." },
    ],
  },
  {
    slug: "brake-repair-cost-guide",
    title: "How Much Does Brake Repair Cost? Here's How to Stop Overpaying",
    metaTitle: "How Much Does Brake Repair Cost? Here's How to Stop Overpaying | Pitcrew",
    metaDescription:
      "Wondering how much brake repair costs? We break down real price ranges for pads, rotors, and full brake jobs — plus the insider tricks to never overpay again.",
    excerpt:
      "Squealing brakes and a vague shop estimate? Here are the real price ranges for pads, rotors, and full brake jobs — plus how to make sure you never overpay.",
    image:
      "https://images.pexels.com/photos/34277923/pexels-photo-34277923.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    category: "Cost Guides",
    date: "2026-06-15",
    readTime: "6 min read",
    seoBody: [
      { p: "You hear that squeal pulling out of the driveway and your stomach drops. Something's off with your brakes — and you already know what comes next: a trip to the shop, a vague estimate, and a bill that somehow feels like it came out of thin air." },
      { p: "So how much does brake repair cost, really? The honest answer is: it depends. But this guide breaks down exactly what you should expect to pay, what shops won't tell you, and how to make sure you're getting a fair deal." },
      { h2: "Why Brake Repair Costs Vary So Much" },
      { p: "Parts quality is one of the biggest variables. Budget brake pads can run $20 a set, while premium ceramic pads top $150 per axle. Labor rates are the other wildcard — a dealership might charge $150/hr, an independent shop $80. Same job, completely different bill. Your vehicle type matters too." },
      { h2: "Real Cost Ranges: What Brake Repair Actually Costs" },
      { ul: [
        "Brake pads only (one axle): $100–$250",
        "Brake pads + rotors (one axle): $250–$500",
        "Full brake job (front and rear): $400–$900+",
        "Brake caliper replacement: add $150–$400 per caliper",
      ] },
      { p: "Always ask for an itemized quote — parts and labor listed separately." },
      { h2: "Signs Your Brakes Need Attention" },
      { ul: [
        "Squealing or squeaking — wear indicators telling you it's time.",
        "Grinding — metal on metal, the pads are gone; don't wait.",
        "Pulling to one side — uneven wear or a stuck caliper.",
        "Soft or spongy pedal — a brake fluid issue; check it immediately.",
        "Vibration when braking — often warped rotors.",
      ] },
      { h2: "What Shops Don't Tell You" },
      { ul: [
        "Unnecessary extras bundled into your quote.",
        "Vague estimates with suspiciously wide ranges.",
        "Labor rate markups — always get at least 3 quotes from different types of shops.",
      ] },
      { h2: "How Much Does Brake Repair Cost Near You? Use Pitcrew to Find Out" },
      { p: "Instead of calling around blindly, describe your symptoms once on Pitcrew and let vetted local mechanics compete with their best bids. You compare prices, reviews, and availability in one place." },
      { h2: "How Pitcrew Works" },
      { ul: [
        "Describe your symptoms — no mechanic jargon required.",
        "Get a free AI diagnostic report instantly.",
        "Receive competitive bids from vetted local mechanics in your area.",
        "Pick the shop that fits your budget. No pressure, no surprises.",
      ] },
      { h2: "Stop Guessing. Start Comparing." },
      { p: "Get your free AI diagnostic and competitive brake repair bids. Your wallet will thank you." },
      { p: "Your Whole Team. One Click." },
    ],
  },
  {
    slug: "check-engine-light-what-it-means",
    title: "Check Engine Light On? Here's What It Could Mean (And How to Get a Free Diagnosis)",
    metaTitle:
      "Check Engine Light On? Here's What It Could Mean (And How to Get a Free Diagnosis)",
    metaDescription:
      "Check engine light on? Don't panic. Learn the most common causes — from a loose gas cap to a failing catalytic converter — and find out how to get a free AI car diagnostic instantly through Pitcrew.",
    excerpt:
      "That little amber glow doesn't have to mean a thousand-dollar bill. Here are the most common causes of a check engine light — and how to get a free AI diagnosis in two minutes.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/ba703438-84db-4d0b-a475-05d6e2fbcf55/images/784c52a0fa0f2f4c0a058152a665c93cd40ac5730239726a1e7dffc8698e1d08.png",
    category: "Diagnostics",
    date: "2026-06-01",
    readTime: "5 min read",
    seoBody: [
      { p: "You're cruising down the highway when that little amber glow appears on your dashboard. Your stomach drops. The check engine light is on — and suddenly you're imagining thousand-dollar repair bills and days without your car." },
      { p: "Here's the truth: most of the time, it's not as bad as you think. But you do need to take it seriously. This guide walks you through the most common causes, what you should avoid doing, and how to get a free car diagnostic without stepping foot in a shop." },
      { h2: "Why Does the Check Engine Light Come On?" },
      { p: "Your car's onboard computer (ECU) monitors hundreds of sensors. When something falls outside normal parameters, it stores a diagnostic trouble code (DTC) and lights up that warning symbol. It could be serious, or just a loose gas cap." },
      { h2: "The Most Common Check Engine Light Causes (In Plain English)" },
      { ul: [
        "Loose or Faulty Gas Cap — the most common cause and the most harmless. A loose or cracked cap breaks the fuel system seal; tighten or replace it.",
        "Oxygen (O2) Sensor Failure — a failing sensor can drop fuel economy by up to 40% and, left alone, damage your catalytic converter.",
        "Catalytic Converter Issues (Code P0420) — often triggered by neglected O2 sensor or misfire problems; ignore it and you'll likely fail an emissions test.",
        "Mass Airflow (MAF) Sensor Problems — a dirty or failing sensor throws off your air-fuel mix, causing stalling, hesitation, or a rough-running engine.",
        "Spark Plugs or Ignition Coil Failure — worn plugs or coils cause misfires, rough idle, and sluggish acceleration.",
      ] },
      { h2: "What You Should NOT Do" },
      { ul: [
        "Don't ignore it — a small fix now beats a big repair later.",
        "Don't drive hard if the light is flashing — a flashing light means an active misfire that can destroy your catalytic converter.",
        "Don't replace parts blindly without reading the actual diagnostic code.",
      ] },
      { h2: "How to Get a Free Car Diagnostic Without Going to a Shop" },
      { p: "Pitcrew offers a free instant AI car diagnostic — no appointment, no obligation. Describe your symptoms and the AI analyzes the most likely causes in plain English. Free. Two minutes." },
      { h2: "How Pitcrew Works: From Diagnosis to Done" },
      { ul: [
        "Diagnose — describe symptoms or enter your code to get a free AI diagnostic report.",
        "Compare — receive competitive bids from vetted local mechanics.",
        "Choose — compare bids and pick your mechanic. No haggling, no surprises.",
      ] },
      { p: "Your whole team. One click." },
      { h2: "Don't Let That Light Sit — Act on It Today" },
      { p: "Get your free car diagnostic and know exactly what your car is telling you in minutes." },
    ],
  },
];

module.exports = { SITE_URL, BLOG_META };
